/**
 * Native push notifications via `@capacitor-firebase/messaging`.
 *
 * Why this plugin rather than `@capacitor/push-notifications`: the official
 * Capacitor plugin returns the raw **APNs device token** on iOS, but our
 * server (`@ah/cloud/push`) uses `firebase-admin`'s `sendEachForMulticast`,
 * which only accepts **FCM tokens**. `@capacitor-firebase/messaging` wraps
 * the Firebase iOS SDK, exchanges the APNs token for an FCM token, and
 * returns the FCM token uniformly — same shape as Android, same shape as
 * the web `firebase/messaging` SDK, so the backend code path is identical
 * across all three surfaces.
 *
 * iOS pre-requisites (set up in apps/mobile-native):
 *   - `FirebaseApp.configure()` in AppDelegate (auto-pulls `GoogleService-Info.plist`)
 *   - Push Notifications capability + Background Modes → Remote notifications
 *   - APNs Auth Key uploaded to Firebase Cloud Messaging settings
 */

import { isNative, platform } from '../runtime/index.ts';

/**
 * Create the default Android notification channel. Android 8+ requires every
 * displayed notification to belong to a channel — if the channel referenced
 * by the FCM payload (or the manifest's
 * com.google.firebase.messaging.default_notification_channel_id meta-data)
 * doesn't exist, the notification is silently dropped.
 *
 * Call once at app boot. Capacitor Firebase's createChannel is idempotent;
 * calling it after the channel already exists is a no-op. No-op on iOS
 * (no channel concept) and on web.
 *
 * The channel id "default" matches the strings.xml resource referenced in
 * AndroidManifest.xml and the server's FCM android.notification.channel_id.
 */
export async function ensureDefaultNotificationChannel(): Promise<void> {
  if (platform() !== 'android') return;
  try {
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
    await FirebaseMessaging.createChannel({
      id: 'default',
      name: 'Notifications',
      description: 'Replies, event invites, and Superconnector messages.',
      // 4 = IMPORTANCE_HIGH — shows as a heads-up banner with sound. Matches
      // the urgency of an iOS push (which doesn't have a importance toggle).
      importance: 4,
      // 1 = VISIBILITY_PUBLIC — show the full notification content on the
      // lock screen. Users can override per-app in system settings.
      visibility: 1,
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    // Idempotent failure is OK; any other failure is logged but non-fatal.
    if (!/already exists/i.test(msg)) {
      console.warn('[push] ensureDefaultNotificationChannel failed:', msg);
    }
  }
}

export interface PushTokenResult {
  ok: true;
  token: string;
}
export interface PushTokenError {
  ok: false;
  reason: 'permission-denied' | 'unsupported' | 'registration-failed' | string;
}

export interface PushMessage {
  title: string;
  body: string;
  data: Record<string, string>;
}

/**
 * Request push permission and return an FCM token suitable for
 * `sendToTokens()`. Idempotent — calling twice returns the same token
 * (Firebase caches the registration).
 */
export async function requestNativePushToken(): Promise<PushTokenResult | PushTokenError> {
  if (!isNative()) return { ok: false, reason: 'unsupported' };

  const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');

  // @capacitor-firebase/messaging 6.3.x has a known issue on Android: if the
  // user takes more than a few seconds to dismiss the POST_NOTIFICATIONS
  // permission dialog, the saved PluginCall is GC'd and the native
  // permissionsCallback NPEs trying to resolve(null) — the Promise here
  // hangs forever and the UI looks frozen. Race the request against a
  // generous timeout that re-reads the actual permission state via
  // checkPermissions (a fresh PluginCall, so it's not affected by the
  // same bug). If the user has answered the dialog by then, Android has
  // already recorded the grant/deny — we just read it.
  const perm = await Promise.race([
    FirebaseMessaging.requestPermissions(),
    new Promise<{ receive: 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale' }>(
      (resolve) => {
        setTimeout(() => {
          FirebaseMessaging.checkPermissions()
            .then((p) => resolve(p))
            .catch(() => resolve({ receive: 'denied' }));
        }, 45_000);
      },
    ),
  ]);
  if (perm.receive !== 'granted') return { ok: false, reason: 'permission-denied' };

  // On iOS, FirebaseMessaging.getToken() blocks until APNs registration has
  // completed (via method swizzling in the Firebase iOS SDK) and the APNs
  // token has been exchanged for an FCM token. Returns null only when the
  // exchange itself failed — usually a Firebase config mismatch.
  try {
    const { token } = await FirebaseMessaging.getToken();
    if (!token) return { ok: false, reason: 'registration-failed: no token returned' };
    return { ok: true, token };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return { ok: false, reason: `registration-failed: ${msg}` };
  }
}

/** Unregister the current device. Caller should also unregister the token server-side. */
export async function clearNativePushRegistration(): Promise<void> {
  if (!isNative()) return;
  const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
  await FirebaseMessaging.removeAllListeners().catch(() => {});
  await FirebaseMessaging.deleteToken().catch(() => {});
}

/**
 * Foreground message handler. Returns an unsubscribe.
 *
 * On iOS the OS does NOT show a banner while the app is foregrounded — the
 * push arrives as a data event and we're expected to surface it ourselves
 * (e.g. an in-app toast). On Android the OS will show a heads-up unless we
 * intercept here.
 */
export function onNativePushReceived(cb: (msg: PushMessage) => void): () => void {
  if (!isNative()) return () => {};
  let handle: { remove: () => Promise<void> } | null = null;
  let cancelled = false;

  void (async () => {
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
    if (cancelled) return;
    handle = await FirebaseMessaging.addListener('notificationReceived', (event) => {
      const n = event.notification;
      cb({
        title: n.title ?? '',
        body: n.body ?? '',
        data: (n.data ?? {}) as Record<string, string>,
      });
    });
  })();

  return () => {
    cancelled = true;
    void handle?.remove();
  };
}

/**
 * Tap / action handler. Fires when the user taps a notification while the app
 * is backgrounded or killed. Use the `data.url` to route.
 */
export function onNativePushTapped(cb: (msg: PushMessage) => void): () => void {
  if (!isNative()) return () => {};
  let handle: { remove: () => Promise<void> } | null = null;
  let cancelled = false;

  void (async () => {
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
    if (cancelled) return;
    handle = await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      const n = event.notification;
      cb({
        title: n.title ?? '',
        body: n.body ?? '',
        data: (n.data ?? {}) as Record<string, string>,
      });
    });
  })();

  return () => {
    cancelled = true;
    void handle?.remove();
  };
}
